import type {
  BillingReportSummary,
  CustomerReportSummary,
  MandateReportSummary,
  NotificationReportSummary,
  OperationalDashboardView,
  ReportDateRangeQuery,
  ReportType,
} from '@solar/contracts';

import { webConfig } from '../../config/env';
import { apiClient } from '../../lib/api-client';
import { toQuery } from '../../lib/api-query';
import type { StaffDashboardVariant } from '../auth/portal-access';
import { getAccessToken } from '../auth/token-store';

export type ReportSummary =
  | CustomerReportSummary
  | BillingReportSummary
  | MandateReportSummary
  | NotificationReportSummary
  | Record<string, unknown>;

export const reportingApi = {
  getDashboard(): Promise<OperationalDashboardView> {
    return reportingApi.getDashboardByVariant('overview');
  },

  getDashboardByVariant(variant: StaffDashboardVariant): Promise<OperationalDashboardView> {
    return apiClient.get(`/admin/dashboard/${variant}`);
  },

  getReport(type: ReportType, query: ReportDateRangeQuery = {}): Promise<ReportSummary> {
    return apiClient.get(`/admin/reports/${type}${toQuery({ ...query })}`);
  },

  async exportReport(type: ReportType, query: ReportDateRangeQuery = {}): Promise<void> {
    const path = `/admin/reports/${type}/export${toQuery({ ...query })}`;
    const token = getAccessToken();

    const response = await fetch(`${webConfig.apiBaseUrl}/${path.replace(/^\/+/, '')}`, {
      credentials: 'include',
      headers: {
        Accept: 'text/csv',
        ...(token !== null && token !== '' ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error('Export failed. Please try again.');
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${type}-report.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  },
};
